<script setup lang="ts">
import BreadCrumb from "@/components/BreadCrumb.vue";
import LoadingModal from "@/components/LoadingModal.vue";
import { useAuth } from "@/composables/useAuth";
import useFuse from "@/composables/useFuse";
import removeStringSpaces from "@/helpers/removeStringSpaces";
import { useQueryClient } from "@tanstack/vue-query";
import { groupBy } from "lodash";
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useToast } from "vue-toastification";
import useInternalUser from "../composables/useInternalUser";
import useUserInternalAbilities from "../composables/useUserInternalAbilities";

const route = useRoute();

const queryClient = useQueryClient();
const toast = useToast();

const { userAbilities, isLoading, postUserAbilityAsync } =
  useUserInternalAbilities({
    params: {
      userId: route.params.id.toString(),
    },
    immediate: true,
  });

const { user, isLoading: loadingUser } = useInternalUser({
  id: route.params.id.toString(),
  immediate: true,
});

const { abilities: actorAbilities } = useAuth();

const searchQuery = ref("");
const activeModule = ref<string | null>(null);
const modalSearch = ref("");
const saveSuccess = ref(false);

const filteredAbilities = computed(() => {
  if (actorAbilities.value?.some((a) => a.name === "*")) return userAbilities.value;
  return userAbilities.value.filter((ability) =>
    actorAbilities.value.some(
      (a) => a.name === ability.name && a.entityType === ability.entityType,
    ),
  );
});

const activeCount = computed(() => filteredAbilities.value.filter((a) => a.checked).length);
const totalCount = computed(() => filteredAbilities.value.length);
const progressPct = computed(() =>
  totalCount.value ? Math.round((activeCount.value / totalCount.value) * 100) : 0
);

const allGroupedByModule = computed(() => groupBy(filteredAbilities.value, "module"));

const visibleModules = computed(() => {
  if (!searchQuery.value) return Object.keys(allGroupedByModule.value);
  const fuse = useFuse(filteredAbilities.value, {
    keys: [{ name: "title", weight: 0.7 }, { name: "name", weight: 0.3 }],
    threshold: 0.2,
    shouldSort: true,
  });
  const matched = fuse.search(removeStringSpaces(searchQuery.value)).map((r) => r.item);
  return Object.keys(groupBy(matched, "module"));
});

const moduleActiveCount = (module: string) =>
  (allGroupedByModule.value[module] ?? []).filter((a) => a.checked).length;

const moduleTotal = (module: string) => (allGroupedByModule.value[module] ?? []).length;

const MODULE_ICONS: Record<string, string> = {
  Company: "business", User: "person", Request: "travel_explore",
  Cef: "receipt_long", Approval: "approval_delegation",
  Policy: "policy", Role: "shield_person", "*": "admin_panel_settings",
};
const moduleIcon = (name: string) => MODULE_ICONS[name] ?? "lock";

// El backend nombra este módulo "Cef"; en UI se muestra como "Itinerarios"
const MODULE_DISPLAY_NAMES: Record<string, string> = { Cef: "Itinerarios" };
const moduleDisplayName = (name: string) => MODULE_DISPLAY_NAMES[name] ?? name;

const modulePermissions = computed(() => {
  if (!activeModule.value) return [];
  const perms = allGroupedByModule.value[activeModule.value] ?? [];
  if (!modalSearch.value) return perms;
  const fuse = useFuse(perms, {
    keys: [{ name: "title", weight: 1 }],
    threshold: 0.3,
    shouldSort: true,
  });
  return fuse.search(removeStringSpaces(modalSearch.value)).map((r) => r.item);
});

const getPermissionSubGroup = (name: string): string => {
  const parts = name.split("-");
  if (parts.length <= 1) return "";
  const sub = parts.slice(1).join("-");
  return sub === "any" ? "" : sub;
};

const modulePermissionGroups = computed(() => {
  const grouped = groupBy(modulePermissions.value, (p) => getPermissionSubGroup(p.name));
  const general = grouped[""] ?? [];
  const rest = Object.entries(grouped)
    .filter(([key]) => key !== "")
    .sort(([a], [b]) => a.localeCompare(b));
  return [
    ...(general.length ? [{ key: "", label: "General", items: general }] : []),
    ...rest.map(([key, items]) => ({
      key,
      label: key
        .replace(/-/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/\b\w/g, (c) => c.toUpperCase()),
      items,
    })),
  ];
});

const toggleAllInModule = (module: string, val: boolean) => {
  (allGroupedByModule.value[module] ?? []).forEach((a) => { a.checked = val; });
};

const togglePermission = (id: number) => {
  const a = userAbilities.value.find((x) => x.id === id);
  if (!a) return;
  a.checked = !a.checked;
  if (!a.name.includes("-")) {
    userAbilities.value.forEach((item) => {
      if (item.name.includes(a.name) && item.name.includes("-") && item.entityType === a.entityType)
        item.checked = a.checked;
    });
  } else {
    userAbilities.value.forEach((item) => {
      if (
        !item.name.includes("-") &&
        item.entityType === a.entityType &&
        item.name.toLocaleLowerCase() === a.name.split("-")[0].toLocaleLowerCase() &&
        a.checked
      ) item.checked = true;
    });
  }
};

const openModule = (module: string) => {
  activeModule.value = module;
  modalSearch.value = "";
};

const handleSave = async () => {
  const userAbilitiesToUpdate = userAbilities.value
    .filter((item) => item.checked)
    .map((item) => item.id);

  await postUserAbilityAsync({
    userId: route.params.id.toString(),
    abilities: userAbilitiesToUpdate,
    toast,
  });

  await queryClient.invalidateQueries(["userAbilities", route.params.id.toString()]);
  saveSuccess.value = true;
  setTimeout(() => { saveSuccess.value = false; }, 2000);
};

const pages = ref<any[]>([]);

watch(
  user,
  (value) => {
    if (value) {
      pages.value = [
        { name: "Usuarios", to: "/users", current: false },
        {
          name: `Permisos de ${value.fullName}`,
          to: {
            name: "users.permissions.internal",
            params: { id: value.id },
          },
          current: true,
        },
      ];
    }
  },
  { immediate: true },
);
</script>

<template>
  <BreadCrumb :pages="pages" v-if="!loadingUser" />

  <div class="my-6 mb-10 mx-auto max-w-6xl px-4 md:px-6">
    <!-- Header card -->
    <div class="bg-white rounded-2xl border border-gray-200 shadow-sm mb-5 overflow-hidden">
      <div class="px-6 py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 rounded-2xl bg-[#021F58] flex items-center justify-center flex-shrink-0 text-white text-lg font-bold">
            {{ user?.fullName?.charAt(0)?.toUpperCase() ?? '?' }}
          </div>
          <div>
            <h1 class="text-lg font-bold text-gray-900" v-if="!loadingUser">{{ user?.fullName }}</h1>
            <div v-else class="h-5 w-40 bg-gray-100 rounded animate-pulse" />
            <div class="flex items-center gap-2 mt-1">
              <span class="text-xs text-gray-400">{{ activeCount }}/{{ totalCount }} permisos activos</span>
              <span
                class="text-[10px] font-bold px-2 py-0.5 rounded-full"
                :class="progressPct === 0 ? 'bg-gray-100 text-gray-400' : progressPct === 100 ? 'bg-emerald-100 text-emerald-700' : 'bg-[#021F58]/10 text-[#021F58]'"
              >{{ progressPct }}%</span>
            </div>
          </div>
        </div>
        <button
          type="button"
          class="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 self-start sm:self-auto"
          :class="saveSuccess ? 'bg-emerald-500 text-white' : 'bg-[#021F58] text-white hover:bg-[#021F58]/90'"
          @click="handleSave"
        >
          <span class="material-symbols-outlined text-base leading-none">{{ saveSuccess ? 'check' : 'save' }}</span>
          {{ saveSuccess ? 'Guardado' : 'Guardar cambios' }}
        </button>
      </div>
      <div class="h-1 bg-gray-100">
        <div
          class="h-full transition-all duration-500"
          :class="progressPct === 100 ? 'bg-emerald-500' : 'bg-[#021F58]'"
          :style="{ width: `${progressPct}%` }"
        />
      </div>
    </div>

    <!-- Search -->
    <div class="relative mb-4">
      <span class="absolute left-3.5 top-1/2 -translate-y-1/2 material-symbols-outlined text-sm text-gray-400 leading-none">search</span>
      <input
        v-model="searchQuery"
        type="text"
        placeholder="Buscar módulo o permiso..."
        class="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#021F58]/20 shadow-sm placeholder-gray-400"
      />
    </div>

    <!-- Module list -->
    <div v-if="!isLoading" class="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      <div v-if="!visibleModules.length" class="flex flex-col items-center justify-center py-12 text-gray-400">
        <span class="material-symbols-outlined text-3xl mb-2">search_off</span>
        <p class="text-sm">Sin resultados</p>
      </div>

      <div v-else class="p-3 space-y-1">
        <button
          v-for="module in visibleModules"
          :key="module"
          type="button"
          class="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left transition-all duration-150 border group"
          :class="moduleActiveCount(module) > 0
            ? 'bg-[#021F58]/5 border-[#021F58]/15 hover:bg-[#021F58]/10'
            : 'border-transparent hover:bg-gray-50 hover:border-gray-200'"
          @click="openModule(module)"
        >
          <div
            class="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors"
            :class="moduleActiveCount(module) > 0 ? 'bg-[#021F58]/10' : 'bg-gray-100'"
          >
            <span
              class="material-symbols-outlined text-xs leading-none transition-colors"
              :class="moduleActiveCount(module) > 0 ? 'text-[#021F58]' : 'text-gray-400'"
            >{{ moduleIcon(module) }}</span>
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-center justify-between gap-1 mb-1">
              <p class="text-xs font-semibold text-gray-900 truncate">{{ moduleDisplayName(module) }}</p>
              <span
                class="text-[10px] tabular-nums flex-shrink-0"
                :class="moduleActiveCount(module) > 0 ? 'text-[#021F58]/60' : 'text-gray-400'"
              >{{ moduleActiveCount(module) }}/{{ moduleTotal(module) }}</span>
            </div>
            <div
              class="h-1 rounded-full overflow-hidden"
              :class="moduleActiveCount(module) > 0 ? 'bg-[#021F58]/10' : 'bg-gray-100'"
            >
              <div
                class="h-full rounded-full transition-all duration-500"
                :class="moduleActiveCount(module) > 0 ? 'bg-[#021F58]' : 'bg-gray-300'"
                :style="{ width: `${moduleTotal(module) ? (moduleActiveCount(module) / moduleTotal(module)) * 100 : 0}%` }"
              />
            </div>
          </div>
          <span
            class="material-symbols-outlined text-xs leading-none flex-shrink-0 transition-colors"
            :class="moduleActiveCount(module) > 0 ? 'text-[#021F58]/30 group-hover:text-[#021F58]/60' : 'text-gray-300 group-hover:text-gray-400'"
          >chevron_right</span>
        </button>
      </div>
    </div>

    <!-- Skeleton -->
    <div v-else class="bg-white rounded-2xl border border-gray-200 shadow-sm p-3 space-y-1">
      <div v-for="i in 6" :key="i" class="h-12 rounded-xl bg-gray-100 animate-pulse" />
    </div>
  </div>

  <LoadingModal :when="loadingUser" />

  <Teleport to="body">
    <Transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div v-if="activeModule" class="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div class="absolute inset-0 bg-black/40 backdrop-blur-sm" @click="activeModule = null" />

        <div class="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
          <!-- Modal header -->
          <div class="px-5 py-4 flex items-center justify-between flex-shrink-0 border-b border-gray-100">
            <div class="flex items-center gap-3">
              <div class="w-9 h-9 rounded-xl bg-[#021F58]/10 flex items-center justify-center">
                <span class="material-symbols-outlined text-[#021F58] leading-none">{{ moduleIcon(activeModule) }}</span>
              </div>
              <div>
                <h3 class="text-sm font-bold text-gray-900">{{ moduleDisplayName(activeModule) }}</h3>
                <p class="text-xs text-gray-400">
                  {{ moduleActiveCount(activeModule) }}/{{ moduleTotal(activeModule) }} activos
                </p>
              </div>
            </div>
            <div class="flex items-center gap-2">
              <button
                type="button"
                class="text-xs font-semibold px-2.5 py-1 rounded-lg border transition-colors"
                :class="moduleActiveCount(activeModule) < moduleTotal(activeModule)
                  ? 'border-[#021F58]/20 text-[#021F58] bg-[#021F58]/5 hover:bg-[#021F58]/10'
                  : 'border-red-200 text-red-500 bg-red-50 hover:bg-red-100'"
                @click="toggleAllInModule(activeModule, moduleActiveCount(activeModule) < moduleTotal(activeModule))"
              >
                {{ moduleActiveCount(activeModule) < moduleTotal(activeModule) ? 'Todos' : 'Ninguno' }}
              </button>
              <button
                type="button"
                class="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:bg-gray-50 transition-colors"
                @click="activeModule = null"
              >
                <span class="material-symbols-outlined text-base leading-none">close</span>
              </button>
            </div>
          </div>

          <!-- Modal search -->
          <div class="px-4 py-3 border-b border-gray-100 flex-shrink-0">
            <div class="relative">
              <span class="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-sm text-gray-400 leading-none">search</span>
              <input
                v-model="modalSearch"
                type="text"
                placeholder="Filtrar permisos..."
                class="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#021F58]/20 bg-gray-50 placeholder-gray-400"
              />
            </div>
          </div>

          <!-- Modal permissions -->
          <div class="overflow-y-auto flex-1 px-4 py-4 space-y-5">
            <div v-if="!modulePermissions.length" class="flex flex-col items-center justify-center py-8 text-gray-400">
              <span class="material-symbols-outlined text-2xl mb-1">search_off</span>
              <p class="text-xs">Sin resultados</p>
            </div>

            <div v-for="group in modulePermissionGroups" :key="group.key">
              <p class="text-[10px] font-bold uppercase tracking-widest text-gray-400 px-1 mb-2">{{ group.label }}</p>
              <div class="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                <button
                  v-for="permission in group.items"
                  :key="permission.id"
                  type="button"
                  class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all duration-150 text-left"
                  :class="permission.checked
                    ? 'bg-[#021F58] border-[#021F58] text-white'
                    : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700 hover:bg-gray-50'"
                  @click="togglePermission(permission.id)"
                >
                  <span v-if="permission.checked" class="material-symbols-outlined text-[10px] leading-none flex-shrink-0">check</span>
                  <span class="truncate">{{ permission.label || permission.title }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
